import React, { useState } from "react";
import { LuChevronRight, LuPencil, LuPlus } from "react-icons/lu";
import { useNavigate, useParams } from "react-router-dom";

import { Card } from "../../../../../../../../components/card/Card";
import { LottieLoading } from "../../../../../../../../components/graphics/LottieLoading";
import { Button } from "../../../../../../../../components/newUI/button";
import { ContactNoteModel } from "../../../../../../../../factories/contacts/models/contact-note.models";
import { getLinkedFirm } from "../../../../../../../../sessionStorage/sessionStorage";
import {
  useDeleteContactNoteMutation,
  useGetContactCustomersNotesQuery,
} from "../../../../../../../../slices/ContactNotesSlice";
import { useGetContactQuery } from "../../../../../../../../slices/ContactsSlice";
import { useDeleteWorkNoteMutation } from "../../../../../../../../slices/WorkNotesSlice";
import { IWorkNote } from "../../../../../../../../types/types";
import {
  getNoteAuthorColor,
  getNoteAuthorName,
  mapNoteToViewModel,
  newestNotes,
  NotePreviewRow,
  useCanEditNote,
  useNoteDeleteConfirm,
} from "../../../../../../components/notes";
import { NoteViewModal } from "../../../../../../components/NoteViewModal";
import { ProfileWidgetEmptyState } from "../../../../../../components/profile";
import { ContactNoteModal } from "../../../../../../modals/ContactNoteModal";
import ErrorPopup from "../../../../../../modals/ErrorPopup";

export const ContactNotesWidget = () => {
  const navigate = useNavigate();
  const { customerId } = useParams<{ customerId: string }>();
  const orgId = getLinkedFirm()?.orgId || "";

  const { data: contact } = useGetContactQuery(
    {
      customerId: customerId || "",
      orgId: orgId,
    },
    { skip: !customerId || !orgId },
  );

  // Same list as the Notes tab (`search-notes`): the client's own notes AND
  // its work items' notes, so "Across all work for this client" is true.
  const { data: allNotes, isFetching } = useGetContactCustomersNotesQuery(
    { orgId, customerId: customerId || "", query: "" },
    { skip: !customerId || !orgId },
  );
  const contactNotes = newestNotes(allNotes);
  const [deleteContactNote] = useDeleteContactNoteMutation();
  const [deleteWorkNote] = useDeleteWorkNoteMutation();
  // Each row's own id is `noteId`; work notes are deleted through the work.
  const deleteNote = async (note: ContactNoteModel) => {
    const id = note.noteId || note.id;
    try {
      if (note.workId) {
        await deleteWorkNote({ id, workId: note.workId } as IWorkNote).unwrap();
      } else {
        await deleteContactNote({ ...note, id }).unwrap();
      }
    } catch {
      setIsOpenError(true);
    }
  };
  const { requestDelete, confirmModal: confirmDeleteModal } =
    useNoteDeleteConfirm<ContactNoteModel>(deleteNote, (note) => note.title);

  const [isOpenAdd, setIsOpenAdd] = useState(false);
  const [isOpenError, setIsOpenError] = useState(false);
  const [currentNote, setCurrentNote] = useState<ContactNoteModel | null>(null);
  const [viewNote, setViewNote] = useState<ContactNoteModel | null>(null);
  const canEdit = useCanEditNote();
  const [isWork, setIsWork] = useState(false);
  const handleOpenAddModal = () => {
    setIsOpenAdd(true);
    setIsWork(false);
    setCurrentNote(null);
  };

  return (
    <>
      <Card extraClasses={"overflow-hidden"}>
        <div className={"flex h-[386px] flex-col p-[22px]"}>
          <div
            className={"mb-4 flex shrink-0 items-start justify-between gap-4"}>
            <span className={"flex flex-col gap-0.5"}>
              <span className={"text-[17px] font-extrabold text-[#1F2433]"}>
                Notes
              </span>
              <span className={"text-[11.5px] font-semibold text-[#9097A8]"}>
                Across all work for this client
              </span>
            </span>
            <span className={"flex shrink-0 items-center gap-3"}>
              <Button
                type={"button"}
                variant={"ghost"}
                size={"sm"}
                autoWidth
                rightIcon={<LuChevronRight size={14} strokeWidth={2.2} />}
                className={
                  "h-auto p-0 text-[13px] font-bold text-[#6B4EFF] hover:bg-transparent"
                }
                onClick={() => navigate(`/contact/${customerId}/notes`)}>
                View all
              </Button>
              <Button
                type={"button"}
                variant={"outline"}
                size={"sm"}
                iconOnly
                autoWidth
                leftIcon={<LuPlus size={16} strokeWidth={2.2} />}
                className={
                  "h-8 w-8 rounded-lg border-[#E4E7EC] p-0 text-[#6B4EFF] hover:bg-[#F4F2FF]"
                }
                onClick={handleOpenAddModal}
              />
            </span>
          </div>
          <div className={"flex min-h-0 flex-1 flex-col"}>
            {isFetching ? (
              <div className={"p-4"}>
                <LottieLoading />
              </div>
            ) : !contactNotes?.length ? (
              <ProfileWidgetEmptyState
                icon={<LuPencil size={28} strokeWidth={1.7} />}
                title={"No notes"}
              />
            ) : (
              <div
                className={
                  "mr-[-22px] min-h-0 flex-1 overflow-y-auto pr-[22px]"
                }>
                {contactNotes?.map((note) => (
                  <NotePreviewRow
                    key={note?.noteId}
                    note={mapNoteToViewModel(note)}
                    onView={() => setViewNote(note)}
                    onEdit={
                      !canEdit(note)
                        ? undefined
                        : () => {
                            setCurrentNote({ ...note, noteId: note.noteId });
                            setIsWork(!!note.workName);
                            setIsOpenAdd(true);
                          }
                    }
                    onDelete={() => requestDelete(note)}
                  />
                ))}
              </div>
            )}
          </div>
        </div>
      </Card>
      {isOpenAdd && (
        <ContactNoteModal
          isActive={isOpenAdd}
          isWork={isWork}
          onClose={() => setIsOpenAdd(false)}
          contactId={contact?.contactId || ""}
          defaultValues={currentNote || undefined}
        />
      )}
      {viewNote && (
        <NoteViewModal
          note={{
            id: viewNote.noteId || viewNote.id,
            title: viewNote.title,
            workId: viewNote.workId,
            workName: viewNote.workName,
            note: viewNote.note,
            createdAt: viewNote.createdAt,
            authorName: getNoteAuthorName(viewNote),
            authorColor: getNoteAuthorColor(viewNote),
          }}
          onClose={() => setViewNote(null)}
          onEdit={
            !canEdit(viewNote)
              ? undefined
              : () => {
                  setCurrentNote(viewNote);
                  setIsWork(!!viewNote.workName);
                  setViewNote(null);
                  setIsOpenAdd(true);
                }
          }
        />
      )}
      <ErrorPopup
        closePopup={() => setIsOpenError(false)}
        isOpen={isOpenError}
      />
      {confirmDeleteModal}
    </>
  );
};
